"use client";

import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import Link from "next/link";

import ChevronRight from "@/assets/svgs/ChevronRight";
import useIsMobile from "@/app/helpers/useIsMobile";

import "./ButtonAnimated.css";

function ButtonAnimated({
  onClick,
  onMouseDown,
  onPointerDown,
  text = "Auto Bewerten",
  width = "266px",
  height = "64px",
  borderColor = "transparent",
  backgroundColor = "#242424",
  hoverBackgroundColor = "#3a3a3a",
  hoverBorderColor = "transparent",
  color = "#fff",
  hoverColor = "#fff",
  fontSize = "22px",
  textDecoration = "none",
  fontWeight = "bold",
  textAlign = "center",
  secondary = false,
  className = "",
  hideChevron = false,
  hideMaxWidth = false,
  link,
  disabled = false,
  loading = false,
  // Loading-Optik konfigurierbar: Default = dunkler Primary-Look (grauer Button +
  // weisser Spinner, wie Weiter). Helle/sekundaere Buttons (z. B. Zurück)
  // ueberschreiben das mit hellem Hintergrund + dunklem Spinner (Referenz-Stil).
  loadingBackgroundColor = "#a3a3a3",
  // Optionaler Loading-State beim Klick (fuer Navigations-Buttons): zeigt
  // sofort den Spinner, bis die Zielseite laedt bzw. die Komponente
  // unmountet. Der "loading"-Prop ueberschreibt/ergaenzt das weiterhin.
  loadingOnClick = false,
  spinnerColor,
  reversed = false,
  removPadding = false,
  maxWidth = "266px",
  gap = "8px",
  // Mobile: Pfeil-Animation bei Tap (Desktop nutzt :hover). Default an; Buttons
  // mit Loading-State (Fahrzeugprofil) setzen tapAnimate={false}.
  tapAnimate = true,
}) {
  const isMobile = useIsMobile();

  const [allowHover, setAllowHover] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Mobile-Tap-Animationsflag (Klasse `tap-animate` am Button). Den Neustart der
  // CSS-Animation bei jedem Tap erzwingt der tapAnimationKey (kontrollierter
  // Remount des Chevron-Spans) – kein rAF, kein erzwungenes Reflow (frueher
  // verursachte document.body.offsetHeight das "Camera-Flicker" auf Foto-Seiten).
  // Der Key aendert sich NUR bei Mobile-pointerdown, nie bei Desktop-Hover ->
  // die Hover-Animation bleibt unberuehrt.
  const [tapAnimating, setTapAnimating] = useState(false);
  const [tapAnimationKey, setTapAnimationKey] = useState(0);

  const [clickLoading, setClickLoading] = useState(false);
  const effectiveLoading = loading || clickLoading;

  const handleClick = (e) => {
    if (loadingOnClick && !effectiveLoading) {
      // flushSync: Spinner rendern, bevor die Navigation die Seite wechselt.
      flushSync(() => setClickLoading(true));
    }
    onClick?.(e);
  };

  const shouldUseSecondaryDefaults =
    secondary &&
    backgroundColor === "#242424" &&
    hoverBackgroundColor === "#3a3a3a" &&
    borderColor === "transparent" &&
    hoverBorderColor === "transparent" &&
    color === "#fff" &&
    hoverColor === "#fff";

  const resolvedBackgroundColor = shouldUseSecondaryDefaults
    ? "#ffffff"
    : backgroundColor;
  const resolvedHoverBackgroundColor = shouldUseSecondaryDefaults
    ? "#f2f2f2"
    : hoverBackgroundColor;
  const resolvedBorderColor = shouldUseSecondaryDefaults
    ? "rgba(0, 0, 0, 0.1)"
    : borderColor;
  const resolvedHoverBorderColor = shouldUseSecondaryDefaults
    ? "rgba(0, 0, 0, 0.1)"
    : hoverBorderColor;
  const resolvedColor = shouldUseSecondaryDefaults ? "#242424" : color;
  const resolvedHoverColor = shouldUseSecondaryDefaults ? "#242424" : hoverColor;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setAllowHover(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const buttonStyle = {
    width: width || "100%",
    minWidth: width,
    height,
    maxWidth: hideMaxWidth ? "unset" : maxWidth,
    padding: removPadding ? "0" : "0 12px",
    display: "flex",
    alignItems: "center",
    justifyContent:
      { left: "flex-start", center: "center", right: "flex-end" }[textAlign] ||
      "center",
    gap,
    border: `2px solid ${
      allowHover && isHovered ? resolvedHoverBorderColor : resolvedBorderColor
    }`,
    backgroundColor: effectiveLoading
      ? loadingBackgroundColor
      : allowHover && isHovered
      ? resolvedHoverBackgroundColor
      : resolvedBackgroundColor,
    color: allowHover && isHovered ? resolvedHoverColor : resolvedColor,
    opacity: disabled && !effectiveLoading ? 0.4 : 1,
    cursor: disabled || effectiveLoading ? "not-allowed" : "pointer",
    pointerEvents: disabled || effectiveLoading ? "none" : "auto",
    textDecoration,
    textUnderlineOffset: "4px",
  };

  const textStyle = {
    color: allowHover && isHovered ? resolvedHoverColor : resolvedColor,
    fontSize,
    fontWeight,
    textDecoration: "none",
    lineHeight: 1,
  };

  const handleMouseEnter = () => {
    if (allowHover && !isMobile) setIsHovered(true);
  };
  const handleMouseLeave = () => {
    if (allowHover && !isMobile) setIsHovered(false);
  };

  // Tap-Animation (nur Mobile, nur Buttons mit sichtbarem Chevron und ohne
  // Loading-State). Neustart per tapAnimationKey (Chevron-Remount) -> kein
  // erzwungenes Layout-Read, damit auf Foto-/Kamera-Seiten kein Flicker
  // entsteht. Cleanup via onAnimationEnd am Chevron.
  const handlePointerDown = (e) => {
    onPointerDown?.(e);
    if (!isMobile || disabled || effectiveLoading || hideChevron || !tapAnimate) return;
    setTapAnimating(true);
    setTapAnimationKey((key) => key + 1); // Remount -> Animation startet neu
  };
  const handleChevronAnimationEnd = () => {
    setTapAnimating(false);
  };

  // WICHTIG: KEINE im Render-Body definierten Komponenten (vorher <Content/> und
  // <Chevron/>). Als JSX-Element-Typ wechselt deren Funktions-Identitaet bei jedem
  // Re-Render -> React unmountet/remountet den Chevron-Span -> die :hover-CSS-
  // Animation startet bei jedem Remount neu (sichtbar als mehrfaches "Springen" des
  // Pfeils, wenn der Parent waehrend des Hoverns mehrfach re-rendert).
  // Direkt eingebettetes JSX bleibt ein stabiler Knoten (in-place reconciled) ->
  // Animation laeuft genau einmal pro Hover.
  const chevronNode = hideChevron ? null : (
    <span
      key={tapAnimationKey}
      className={`Button-Animated-Chevron ${reversed ? "reversed" : ""}`}
      aria-hidden="true"
      onAnimationEnd={handleChevronAnimationEnd}
    >
      <ChevronRight width={28} height={28} />
    </span>
  );

  // Loading-State im Referenz-Stil: rotierender Spinner statt Text/Chevron. Default
  // = weisser Spinner (auf dem dunklen Primary-Loading-BG). spinnerColor faerbt
  // den Spinner um (z. B. dunkel fuer den hellen Zurück-Button); die Spur leitet
  // sich als halbtransparente Variante daraus ab.
  const spinnerStyle = spinnerColor
    ? {
        "--spinner-head": spinnerColor,
        "--spinner-track": /^#[0-9a-fA-F]{6}$/.test(spinnerColor)
          ? `${spinnerColor}66`
          : spinnerColor,
      }
    : undefined;
  const content = effectiveLoading ? (
    <span
      className="Button-Animated-Spinner"
      role="status"
      aria-label="Lädt…"
      style={spinnerStyle}
    />
  ) : reversed ? (
    <>
      {chevronNode}
      <span style={textStyle}>{text}</span>
    </>
  ) : (
    <>
      <span style={textStyle}>{text}</span>
      {chevronNode}
    </>
  );

  const ButtonEl = (
    <button
      type="button"
      disabled={disabled || effectiveLoading}
      aria-busy={effectiveLoading || undefined}
      style={buttonStyle}
      className={[
        "Button-Animated-Button",
        secondary ? "secondary" : "",
        className,
        isMobile ? "is-mobile" : "is-desktop",
        isMobile && tapAnimating ? "tap-animate" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      onPointerDown={handlePointerDown}
    >
      {content}
    </button>
  );

  const LinkEl = (
    <Link
      href={link}
      className={[
        "Button-Animated-Button",
        "as-link",
        secondary ? "secondary" : "",
        className,
        isMobile ? "is-mobile" : "is-desktop",
        isMobile && tapAnimating ? "tap-animate" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={buttonStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      aria-disabled={disabled || effectiveLoading || undefined}
      aria-busy={effectiveLoading || undefined}
    >
      {content}
    </Link>
  );

  return link ? LinkEl : ButtonEl;
}

export default ButtonAnimated;
